import { useEffect, useState } from 'react';
import { Plus, Save, Trash2, CalendarClock } from 'lucide-react';
import supabase from '../lib/supabase';
import {
  Section, Badge, Button, EmptyState, Modal, Field, Input, Select, StatusLine,
} from './ui';

type TimeOff = {
  id: string;
  coach_id: string;
  starts_at: string;
  ends_at:   string;
  kind: 'annual_leave' | 'day_off' | 'time_off' | 'sick' | 'other';
  reason: string | null;
};

const KIND_TONE: Record<TimeOff['kind'], 'lime' | 'blue' | 'amber' | 'gray'> = {
  annual_leave: 'lime',
  day_off:      'blue',
  time_off:     'blue',
  sick:         'amber',
  other:        'gray',
};

const KIND_LABEL: Record<TimeOff['kind'], string> = {
  annual_leave: 'Annual leave',
  day_off:      'Day off',
  time_off:     'Time off',
  sick:         'Sick leave',
  other:        'Other',
};

/**
 * Lists upcoming time-off windows for a coach and lets the viewer add or
 * remove them. RLS on coach_time_off already gates writes to: the coach
 * themselves, their home-club managers, and platform admins — so we
 * don't need a client-side role check; the DB will refuse unauthorised
 * writes with a clear 42501 error.
 *
 * `embedded` renders without its own Section wrapper so it can be
 * dropped into a page that already supplies one (e.g. inside a modal
 * on the Coaches page).
 */
export default function TimeOffEditor({
  coachId, heading = 'Time off & leave', embedded = false,
}: { coachId: string; heading?: string; embedded?: boolean }) {
  const [rows, setRows] = useState<TimeOff[]>([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [msg, setMsg] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = async () => {
    setLoading(true);
    const nowIso = new Date().toISOString();
    const { data } = await supabase
      .from('coach_time_off')
      .select('id, coach_id, starts_at, ends_at, kind, reason')
      .eq('coach_id', coachId)
      .gte('ends_at', nowIso)
      .order('starts_at');
    setRows((data as TimeOff[]) ?? []);
    setLoading(false);
  };

  useEffect(() => { load(); }, [coachId]);

  const remove = async (r: TimeOff) => {
    if (!confirm(`Remove ${KIND_LABEL[r.kind].toLowerCase()} starting ${fmtDate(r.starts_at)}?`)) return;
    setMsg(null);
    const { error } = await supabase.from('coach_time_off').delete().eq('id', r.id);
    if (error) setMsg({ kind: 'error', text: error.message });
    else { setMsg({ kind: 'success', text: 'Removed.' }); load(); }
  };

  const content = (
    <>
      {msg && <div className={embedded ? 'mb-3' : 'mb-4'}><StatusLine kind={msg.kind}>{msg.text}</StatusLine></div>}

      {loading ? (
        <div className="p-6 text-sm text-gray-500 text-center">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          icon={CalendarClock}
          title="No upcoming leave"
          description="Add annual leave, a day off, or a partial-day time-off window. Lessons can't be booked inside these windows."
          action={<Button icon={Plus} onClick={() => setAdding(true)}>Add time off</Button>}
        />
      ) : (
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-gray-500">
            <tr className="border-b border-gray-200">
              <th className="text-left py-2 font-semibold">Kind</th>
              <th className="text-left py-2 font-semibold">From</th>
              <th className="text-left py-2 font-semibold">Until</th>
              <th className="text-left py-2 font-semibold">Note</th>
              <th className="py-2"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-b border-gray-50 last:border-0 hover:bg-gray-50/60">
                <td className="py-3">
                  <Badge tone={KIND_TONE[r.kind]}>{KIND_LABEL[r.kind]}</Badge>
                </td>
                <td className="py-3 text-gray-700">{fmtDT(r.starts_at)}</td>
                <td className="py-3 text-gray-700">{fmtDT(r.ends_at)}</td>
                <td className="py-3 text-gray-600">
                  {r.reason || <span className="italic text-gray-400">—</span>}
                </td>
                <td className="py-3 text-right">
                  <Button size="sm" variant="danger" icon={Trash2} onClick={() => remove(r)}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {adding && (
        <TimeOffForm
          coachId={coachId}
          onClose={() => setAdding(false)}
          onSaved={async () => { setAdding(false); await load(); }}
        />
      )}
    </>
  );

  if (embedded) {
    return (
      <div>
        <div className="flex items-center justify-between mb-3">
          <div className="text-sm font-medium text-gray-900">{heading}</div>
          <Button size="sm" icon={Plus} onClick={() => setAdding(true)}>Add time off</Button>
        </div>
        {content}
      </div>
    );
  }

  return (
    <Section
      title={<span className="flex items-center gap-2"><CalendarClock className="w-4 h-4 text-[#2d5016]" /> {heading}</span>}
      subtitle="Block lessons during holidays, annual leave, or partial days away."
      actions={<Button icon={Plus} onClick={() => setAdding(true)}>Add time off</Button>}
    >
      {content}
    </Section>
  );
}

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

function fmtDT(iso: string) {
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function TimeOffForm({
  coachId, onClose, onSaved,
}: { coachId: string; onClose: () => void; onSaved: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [kind, setKind] = useState<TimeOff['kind']>('annual_leave');
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('00:00');
  const [endTime, setEndTime] = useState('23:59');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const start = allDay
      ? new Date(`${startDate}T00:00:00`)
      : new Date(`${startDate}T${startTime}:00`);
    const end = allDay
      ? new Date(`${endDate || startDate}T23:59:59`)
      : new Date(`${endDate || startDate}T${endTime}:00`);
    if (end <= start) {
      setBusy(false);
      setErr('End must be after start.');
      return;
    }
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase.from('coach_time_off').insert({
      coach_id:   coachId,
      starts_at:  start.toISOString(),
      ends_at:    end.toISOString(),
      kind,
      reason:     reason || null,
      created_by: user?.id ?? null,
    });
    setBusy(false);
    if (error) {
      // RLS refusal surfaces as 42501 / "permission denied". Give a
      // more useful message to club staff who aren't allowed to edit a
      // freelance coach's calendar.
      setErr(error.message.toLowerCase().includes('row-level security')
        ? "You don't have permission to edit this coach's calendar."
        : error.message);
      return;
    }
    onSaved();
  };

  return (
    <Modal
      title="Add time off"
      subtitle="Lessons booked during this window will be blocked. Existing bookings are not auto-cancelled."
      onClose={onClose}
    >
      <form onSubmit={submit} className="space-y-4">
        <Field label="Kind">
          <Select value={kind} onChange={(e) => setKind(e.target.value as TimeOff['kind'])}>
            <option value="annual_leave">Annual leave</option>
            <option value="day_off">Day off</option>
            <option value="time_off">Time off (partial day)</option>
            <option value="sick">Sick leave</option>
            <option value="other">Other</option>
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="From">
            <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
          </Field>
          <Field label="Until" hint="Leave blank to match start">
            <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} />
          </Field>
        </div>

        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox" checked={allDay}
            onChange={(e) => setAllDay(e.target.checked)}
            className="w-4 h-4 accent-[#2d5016]"
          />
          All day
        </label>

        {!allDay && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Start time">
              <Input type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} required />
            </Field>
            <Field label="End time">
              <Input type="time" value={endTime} onChange={(e) => setEndTime(e.target.value)} required />
            </Field>
          </div>
        )}

        <Field label="Note" hint="Visible to anyone who can see this coach's calendar.">
          <Input
            value={reason} onChange={(e) => setReason(e.target.value)}
            placeholder="Family holiday, training, etc."
          />
        </Field>

        {err && <StatusLine kind="error">{err}</StatusLine>}

        <div className="flex gap-2 pt-2">
          <Button type="button" variant="secondary" onClick={onClose} className="flex-1">Cancel</Button>
          <Button type="submit" loading={busy} icon={Save} className="flex-1">Save time off</Button>
        </div>
      </form>
    </Modal>
  );
}
